import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { useSidebar } from '@factorialco/factorial-one-react/dist/experimental'
import {
  LayoutContextProvider,
  LiveKitRoom,
  RoomAudioRenderer,
  useCreateLayoutContext,
  usePersistentUserChoices,
} from '@livekit/components-react'
import { useMediaQuery } from 'usehooks-ts'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { usePrompt } from 'modules/core/lib/react-router'

import { MeetingErrorLayout } from 'modules/meetings/components/Layout/MeetingErrorLayout'
import { RoomSkeleton } from 'modules/meetings/components/Skeletons/RoomSkeleton'
import { RoomProvider } from 'modules/meetings/lib/context/RoomContext/RoomProvider'
import { useMeetingDetails } from 'modules/meetings/lib/hooks/useMeetingDetails'
import { useRoomContext } from 'modules/meetings/lib/hooks/useRoomContext'

import { Chat } from './components/Chat'
import { Drawer } from './components/Drawer'
import { Grid } from './components/Grid'
import { RoomControlBar } from './components/RoomControlBar'
import { SideBar } from './components/SideBar'
import { Transcript } from './components/Transcript'

const MeetingRoomPageContent = () => {
  const { id } = useParams<{ id: string }>()
  const { navigation } = useNavigation()
  const { sidebarState, toggleSidebar } = useSidebar()
  const navigate = useNavigate()
  const { setMeetingId, loading, room, error, isChatOpen, isSideBarOpen } = useRoomContext()
  const { loading: pageDetailsLoading } = useMeetingDetails()
  const layoutContext = useCreateLayoutContext()
  const { userChoices } = usePersistentUserChoices()
  const [audioOutputDeviceId, setAudioOutputDeviceId] = useState<string | null>(null)

  const DEFAULT_SIDEBAR_WIDTH = 700
  const MIN_MAIN_CONTENT_WIDTH = 300

  const calculateSidebarWidth = (parentWidth: number) =>
    parentWidth - DEFAULT_SIDEBAR_WIDTH < MIN_MAIN_CONTENT_WIDTH
      ? Math.max(parentWidth - MIN_MAIN_CONTENT_WIDTH, 0)
      : DEFAULT_SIDEBAR_WIDTH

  // Initialize sidebar width based on the current viewport to avoid layout flicker
  const [sidebarWidth, setSidebarWidth] = useState(() =>
    typeof window !== 'undefined' ? calculateSidebarWidth(window.innerWidth) : DEFAULT_SIDEBAR_WIDTH
  )
  const containerRef = useRef<HTMLDivElement | null>(null)

  const isMobile = useMediaQuery('(max-width: 768px)')

  useEffect(() => {
    if (typeof window === 'undefined') return

    const updateSidebarWidth = () => {
      const parentWidth = containerRef.current?.offsetWidth ?? window.innerWidth
      const desiredSidebarWidth = calculateSidebarWidth(parentWidth)
      setSidebarWidth((prev) => (prev !== desiredSidebarWidth ? desiredSidebarWidth : prev))
    }
    updateSidebarWidth()
    window.addEventListener('resize', updateSidebarWidth)
    return () => window.removeEventListener('resize', updateSidebarWidth)
  }, [isChatOpen, isSideBarOpen])

  useEffect(() => {
    if (id) {
      setMeetingId(Number(id))
    }
    return () => {
      setMeetingId(null)
    }
  }, [id, setMeetingId])

  usePrompt(i18n.t('meetings.room.confirm_exit'), () => true)

  useEffect(() => {
    const savedAudioOutputDevice = localStorage.getItem('livekit-audio-output-device')
    setAudioOutputDeviceId(savedAudioOutputDevice)
  }, [])

  useEffect(() => {
    const handleStorageChange = () => {
      const savedAudioOutputDevice = localStorage.getItem('livekit-audio-output-device')
      setAudioOutputDeviceId(savedAudioOutputDevice)
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  // close sidebar when meeting page is loaded
  useEffect(() => {
    if (sidebarState === 'locked') toggleSidebar()
    return () => {
      if (sidebarState === 'hidden') toggleSidebar()
    }
  }, [])

  const handleDisconnect = () => {
    setMeetingId(null)
    // TODO: change this with a hook that retrives the meeting type
    if (id) navigate(navigation.engagement.one_on_ones.meeting.meetId.fullPath({ id }))
  }

  if (loading || pageDetailsLoading) {
    return <RoomSkeleton />
  }

  if (!loading && error) {
    return <MeetingErrorLayout />
  }

  return (
    <Box style={{ width: '100%' }} height="full" position="relative">
      <LiveKitRoom
        serverUrl={room?.serverUrl}
        token={room?.token}
        options={{
          adaptiveStream: true,
          dynacast: true,
          audioOutput: audioOutputDeviceId ? { deviceId: audioOutputDeviceId } : undefined,
        }}
        onConnected={() => {}}
        onError={() => {}}
        audio={
          userChoices?.audioEnabled && {
            deviceId: userChoices?.audioDeviceId,
          }
        }
        video={
          userChoices?.videoEnabled && {
            deviceId: userChoices?.videoDeviceId,
          }
        }
      >
        <LayoutContextProvider value={layoutContext}>
          <Box
            position="absolute"
            style={{ inset: '0px', width: '100%' }}
            overflowY="hidden"
            overflowX="hidden"
            height="full"
          >
            <Box
              ref={containerRef}
              flexDirection={isMobile ? 'column' : 'row'}
              flexGrow
              alignItems="center"
              justifyContent="flexStart"
              position="relative"
              style={{ width: '100%', overflow: 'hidden', willChange: 'transform' }}
            >
              {/* Video content */}
              <Box
                paddingX="s24"
                paddingY="s16"
                height="full"
                style={
                  isMobile
                    ? {
                        width: '100%',
                        height: isChatOpen || isSideBarOpen ? '50%' : '100%',
                        transition: 'height 0.5s ease-in-out',
                        willChange: 'height',
                      }
                    : {
                        width:
                          isChatOpen || isSideBarOpen ? `calc(100% - ${sidebarWidth}px)` : '100%',
                        transition: 'width 0.5s ease-in-out',
                        willChange: 'width',
                      }
                }
              >
                <Grid />
              </Box>
              {/* Chat */}
              <Drawer isMobile={isMobile} isOpen={isChatOpen} sidebarWidth={sidebarWidth}>
                <Chat />
              </Drawer>
              {/* Sidebar */}
              <Drawer isMobile={isMobile} isOpen={isSideBarOpen} sidebarWidth={sidebarWidth}>
                <SideBar />
              </Drawer>
            </Box>

            <Box paddingBottom="s16" style={{ width: '100%' }}>
              <RoomControlBar handleDisconnect={handleDisconnect} />
            </Box>
          </Box>
          <RoomAudioRenderer />
          <Transcript />
        </LayoutContextProvider>
      </LiveKitRoom>
    </Box>
  )
}

const MeetingRoomPage = () => {
  return (
    <RoomProvider>
      <MeetingRoomPageContent />
    </RoomProvider>
  )
}

export default MeetingRoomPage
